import { action, autorun, computed, makeAutoObservable, toJS } from 'mobx'
import { t } from '@lingui/core/macro'

import { downloadItems } from '@jotta/grpc-connect-client/content'
import { getPath } from '@jotta/grpc-js-client/files/getPath'
import {
  create,
  createFolder,
  downloadItem,
  ItemKind,
  ItemType,
} from '@jotta/grpc-js-client/fileService'
import {
  declineShareInvite,
  joinShare,
  leaveShare,
} from '@jotta/grpc-js-client/sharingService'
import { CreateFolderRequest } from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import type { AppError } from '@jotta/types/AppError'
import { handleUnknownError } from '@jotta/types/AppError'
import type { FileActionContext } from '@jotta/types/FileActions'
import { isFileActionContext } from '@jotta/types/schemas/FileActionSchema'
import { showToast } from '@jotta/ui/ToastProvider'
import { env } from '@jotta/utils/env'
import { createPath } from '@jotta/utils/pathUtils'

import { fileUploadManager } from '../ActionManager/FileUploadManager'
import { FileConflictQueue } from '../FileConflictDialog/FileConflictStore'
import type { FolderStore } from '../Folder/FolderStore'
import { PathItemStore } from '../PathItem/PathItemStore'

import type { FileTransferType } from './FileTransferDialog/FileTransferDialog'
import { displayJoinFolderShareSuccessAlert } from './displayJoinFolderShareSuccessAlert'
import type { ActiveFileActionType } from './fileActionConfig'
import { isActiveFileActionType } from './fileActionConfig'
import { transferFiles } from './transferFiles'

export type OnContextMenuSelectParams = {
  path: string
  action: string
  context: string
}

export class FileActionStore {
  // #region Properties (3)

  private _api = {
    create,
    createFolder,
    downloadItem,
  }

  private log

  public activeFileAction:
    | {
        type: 'Disabled'
        context: null
        error: null
        path: null
      }
    | {
        type: ActiveFileActionType
        context: FileActionContext
        error: null | AppError
        path: string
      } = {
    type: 'Disabled',
    context: null,
    error: null,
    path: null,
  }

  // #endregion Properties (3)

  // #region Constructors (1)

  constructor(private folder: FolderStore) {
    this.log = folder.log.extend('action')
    makeAutoObservable<typeof this, 'log' | '_api' | 'api'>(
      this,
      {
        activeFileActionData: computed.struct,
        log: false,
        _api: false,
        api: false,
      },
      {
        autoBind: true,
      },
    )

    autorun(() => {
      if (fileUploadManager.completed > 0) {
        this.folder.refresh()
      }
    })
  }

  // #endregion Constructors (1)

  // #region Public Accessors (4)

  public get actionItems() {
    if (this.activeFileAction.type === 'Disabled') {
      return undefined
    }
    if (
      this.activeFileAction.context === 'SELECTION' &&
      this.folder.selectedChildren.length
    ) {
      return this.folder.selectedChildren
    }
    const item = this.folder.getItemByPath(this.activeFileAction.path)
    if (item) {
      return [item]
    }
    return undefined
  }

  public get activeFileActionData() {
    return toJS(this.activeFileAction)
  }

  public get api() {
    if (env.isProduction) {
      return this._api
    }
    // return this._mockApi
    return this._api
  }

  public get hasActiveAction() {
    return this.activeFileAction.type !== 'Disabled'
  }

  // #endregion Public Accessors (4)

  // #region Public Methods (20)

  public cancelActiveAction(open?: boolean, reason = 'Cancelled') {
    if (!open && this.hasActiveAction) {
      this.log('cancelActiveAction: %s, %s', this.activeFileAction.type, reason)
      this.activeFileAction = {
        type: 'Disabled',
        context: null,
        error: null,
        path: null,
      }
      console.groupEnd()
    } else {
      this.log('cancelActiveAction: skipped')
    }
  }

  public clearActiveFileActionError(open?: boolean) {
    if (!open && this.activeFileAction.error) {
      this.activeFileAction.error = null
    }
  }

  public async createDocument({
    path,
    documentType,
  }: {
    path: string
    documentType: 'docx' | 'xlsx' | 'pptx'
  }) {
    console.group('Create document %s %s', path, documentType)
    const documentTypeToKind: Record<typeof documentType, ItemKind> = {
      docx: ItemKind.WORD,
      xlsx: ItemKind.EXCEL,
      pptx: ItemKind.POWERPOINT,
    }
    if (
      /^New(Word|Excel|Powerpoint)Document$/.test(this.activeFileAction.type)
    ) {
      const log = this.log.extend(`new:${documentType}:${path}`)
      const item = this.folder.pathItem
      if (item.path === path) {
        const fileName = `Untitled.${documentType}`
        const res = await create({
          path,
          name: fileName,
        })
        log('allocated', res)
        const fileItem = new PathItemStore(
          res.name,
          {
            name: res.name,
            path: res.path,
            size: res.bytes,
            kind: documentTypeToKind[documentType],
            type: ItemType.FILE,
          },
          this.folder,
        )
        this.folder.addEntry(fileItem)
        this.folder.refresh()
        this.cancelActiveAction()
        this.dispatch('OpenDocument', 'FOLDER_LIST', fileItem.path)
      }
    }
    console.groupEnd()
  }

  public async createFolder(name: string) {
    const log = this.log.extend('createFolder')
    const folder = this.folder
    const apiPath = folder.apiPath
    if (this.activeFileAction.type !== 'CreateFolder') {
      log('File action type mismatch')
      return
    }
    try {
      log('Creating folder with name %s', name)
      const item = await this.api.createFolder({
        path: createPath(apiPath, name),
        conflictHandler: CreateFolderRequest.ConflictHandler.REJECT_CONFLICTS,
      })
      log('Created', item)
      folder.addItem(item, Date.now())
      this.cancelActiveAction()
    } catch (error) {
      this.setError(error, 'CreateFolder')
    }
  }

  public dispatch(
    action: ActiveFileActionType,
    context: FileActionContext,
    path: string,
  ) {
    if (this.hasActiveAction) {
      this.log(
        'Dispatch %s rejected, %s action in progress',
        action,
        this.activeFileAction.type,
      )
      return
    }
    switch (action) {
      /**
       * Actions that can be dispatched without checking for a pathItem
       */
      case 'Rename':
      case 'AcceptPendingInvite':
      case 'DeclinePendingInvite':
      case 'Restore':
        {
          console.group('Dispatch action %s %s', action, path)
          this.activeFileAction = {
            type: action,
            context,
            error: null,
            path,
          }
        }

        break

      /**
       * Actions that require the current folder to contain a pathitem
       * matching the action path
       */
      default: {
        const item = this.folder.getItemByPath(path)
        if (item) {
          console.group('Dispatch action %s %s', action, path)
          this.activeFileAction = {
            type: action,
            context,
            error: null,
            path,
          }
        } else {
          this.log(
            'Dispatch %s rejected, item for path %s not found',
            action,
            path,
          )
        }
      }
    }
  }

  public acceptPendingInvite(inviteKey: string) {
    const log = this.log.extend(`Accept invite`)
    if (this.activeFileAction.type !== 'AcceptPendingInvite') {
      log('Active file action mismatch')
      return
    }
    const invite = this.folder.root.pendingInvites.find(
      invite => invite.inviteKey === inviteKey,
    )
    if (!invite) {
      log('Invite with key %s not found', inviteKey)
      this.cancelActiveAction()
    }
    joinShare({ inviteKey })
      .then(
        action('joinShareSuccess', shareInfo => {
          const folder = shareInfo?.pathItem?.name || 'folder'
          displayJoinFolderShareSuccessAlert(folder)
          this.folder.refresh()
        }),
        action('joinShareError', err => {
          this.folder.refresh()
        }),
      )
      .finally(this.cancelActiveAction)
  }
  public declinePendingInvite(inviteKey: string) {
    const log = this.log.extend(`Decline invite`)
    if (this.activeFileAction.type !== 'DeclinePendingInvite') {
      log('Active file action mismatch')
      return
    }
    const invite = this.folder.root.pendingInvites.find(
      invite => invite.inviteKey === inviteKey,
    )
    if (!invite) {
      log('Invite with key %s not found', inviteKey)
      this.cancelActiveAction()
    }
    declineShareInvite({ inviteKey })
      .then(
        action('declineShareSuccess', shareInfo => {
          this.folder.refresh()
        }),
        action('declineShareError', err => {
          this.folder.refresh()
        }),
      )
      .finally(this.cancelActiveAction)
  }
  /**
   * The action is happening on the current route
   * Actions that change or remove the route requires navigation when the action succeeds
   */
  get isActiveRouteAction() {
    return this.activeFileAction.path === this.folder.apiPath
  }
  public async downloadItem() {
    const log = this.log.extend(`downloadItem`)
    const useZipDownload =
      this.activeFileAction.path?.toLowerCase() === '/contacts'

    if (useZipDownload) {
      const res = await getPath({ path: '/Contacts' })
      await downloadItems(t`Contacts`, res.item.childrenList)
      this.cancelActiveAction()
      return
    }

    if (this.activeFileAction.type === 'Download') {
      log(this.activeFileAction.path)
      await this.api.downloadItem({
        path: this.activeFileAction.path,
        includeDeleted:
          this.folder.pathItem.isDeleted || this.folder.pathItem.isTrash,
      })
      this.cancelActiveAction()
    }
  }

  public async downloadSelectedItems(name: string, items: PathItemStore[]) {
    if (
      this.activeFileAction.type === 'Download' &&
      this.activeFileAction.context === 'SELECTION'
    ) {
      const canDownloadFolder = this.folder.pathItem.isDownloadable

      const log = this.log.extend(`downloadSelectedItems:${items.length}`)
      // Download the current folder if every item in the list is selected
      if (
        items.length > 1 &&
        this.folder.selection.allSelected &&
        canDownloadFolder
      ) {
        log('start all selected folder download')
        await this.api.downloadItem({
          path: this.folder.pathItem.path,
          includeDeleted: this.folder.pathItem.isDeleted,
        })
        this.cancelActiveAction()
        return
      }
      if (items.length) {
        await downloadItems(name, items)
      } else {
        this.cancelActiveAction(false, 'No items to download')
      }
      this.cancelActiveAction()
    }
  }

  public leaveShare(folderShareId: string) {
    if (this.activeFileAction.type === 'Share') {
      const { isActiveRouteAction } = this
      leaveShare({
        folderShareId,
      }).then(
        action('leaveShareSuccess', () => {
          if (isActiveRouteAction) {
            this.folder.root.router.up()
          }
          this.folder.refresh()
          this.cancelActiveAction()
        }),
        action('leaveShareError', err => {
          this.log(err)
          showToast({
            message: t`An error occured!`,
            hasCloseButton: true,
            duration: Infinity,
          })
        }),
      )
    }
  }

  public onContextMenuSelect({
    path,
    action,
    context,
  }: OnContextMenuSelectParams) {
    this.log('onContextMenuSelect', {
      action,
      path,
      context,
      included: this.folder.ids.has(path),
      isActiveFileActionType: isActiveFileActionType(action),
      isFileActionContext: isFileActionContext(context),
    })

    if (
      path &&
      isActiveFileActionType(action) &&
      isFileActionContext(context)
    ) {
      setTimeout(() => {
        this.dispatch(action, context, path)
      }, 200)
    }
    console.groupEnd()
  }

  public setError(error: unknown, action: ActiveFileActionType) {
    if (this.activeFileAction.type === action) {
      this.activeFileAction.error = handleUnknownError(error)
    }
  }

  public readonly conflicts = new FileConflictQueue()

  public async transferItems({
    transferType,
    filePaths,
    targetDirectoryPath,
  }: {
    transferType: FileTransferType
    filePaths: string[]
    targetDirectoryPath: string
  }) {
    if (transferType === 'MOVE' && this.activeFileAction.type !== 'Move') {
      this.log(
        `Can't move when Active file action is ${this.activeFileAction.type}`,
      )
      return {
        completedSuccessfully: false,
      }
    }
    if (transferType === 'COPY' && this.activeFileAction.type !== 'Copy') {
      this.log(
        `Can't copy when Active file action is ${this.activeFileAction.type}`,
      )
      return {
        completedSuccessfully: false,
      }
    }
    const items = this.actionItems
    if (!items) {
      this.cancelActiveAction(false, 'No items to transfer ')
      return {
        completedSuccessfully: false,
      }
    }

    if (filePaths.length > 1) {
      this.conflicts.hasMultiple = true
    }

    if (transferType === 'MOVE') {
      for (const item of items) {
        item.model.deletedAtMillis = Date.now()
        item.model.submit()
      }
    }

    const errors: unknown[] = []

    const transferResponse = await transferFiles({
      filePaths: filePaths,
      transferType,
      targetDirectoryPath,
      conflictStore: this.conflicts,
      onFileTransferred: async ({ oldPath, fileName, error, result }) => {
        if (error) {
          errors.push(error)
        }
        const item = this.folder.getItemByPath(createPath(oldPath, fileName))
        if (item && error) {
          item.model.deletedAtMillis = 0
          item.model.submit()
        } else if (item && !error && transferType === 'MOVE') {
          this.folder.removeItem(createPath(oldPath, fileName))
        }
        return result
      },
    }).catch(e => {
      this.conflicts.clear()
      this.folder.refresh()
      this.cancelActiveAction()
      throw e
    })

    this.conflicts.clear()
    this.folder.refresh()

    if (errors.length) {
      throw errors
    }

    return transferResponse
  }
  // #endregion Public Methods (20)
}
